import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface StoryboardScene {
  id: number;
  title: string;
  duration: string;
  description: string;
  voiceover: string;
  visualNotes: string;
  image: string;
  mood: string;
}

const storyboardScenes: StoryboardScene[] = [
  {
    id: 1,
    title: "Opening - Brand Reveal",
    duration: "0:00 - 0:05",
    description: "Dramatic fade-in of the House of FMR logo with golden particles",
    voiceover: "\"Where passion meets perfection...\"",
    visualNotes: "Dark background, logo animation, subtle gold sparkles, elegant typography reveal",
    image: "https://public.readdy.ai/ai/img_res/be416317c59b1fdbe07ef5cffd2e1ec2.jpg",
    mood: "Mysterious & Luxurious"
  },
  {
    id: 2,
    title: "The Bar Setup",
    duration: "0:05 - 0:12",
    description: "Sweeping shot of a beautifully arranged cocktail bar with premium spirits",
    voiceover: "\"Welcome to House of FMR, Sydney's premier mobile cocktail experience.\"",
    visualNotes: "Slow pan across bar tools, bottles catching light, ice bucket condensation, warm ambient lighting",
    image: "https://readdy.ai/api/search-image?query=Luxurious%20cocktail%20bar%20setup%20with%20premium%20spirits%20bottles%20arranged%20beautifully%20professional%20bar%20tools%20copper%20shakers%20crystal%20glasses%20warm%20ambient%20lighting%20elegant%20dark%20wood%20background&width=400&height=300&seq=storyboard2&orientation=landscape",
    mood: "Sophisticated & Inviting"
  },
  {
    id: 3,
    title: "The Mixologist",
    duration: "0:12 - 0:20",
    description: "Close-up of hands expertly crafting a signature cocktail",
    voiceover: "\"Master the art of mixology with our expert-led classes.\"",
    visualNotes: "Skilled hands measuring, pouring, shaking - focus on technique and precision, slow motion pour",
    image: "https://readdy.ai/api/search-image?query=Professional%20bartender%20hands%20crafting%20cocktail%20close%20up%20shot%20pouring%20liquid%20into%20shaker%20with%20precision%20elegant%20movements%20warm%20lighting%20professional%20mixology%20technique%20demonstration&width=400&height=300&seq=storyboard3&orientation=landscape",
    mood: "Skillful & Artistic"
  },
  {
    id: 4,
    title: "Class Experience",
    duration: "0:20 - 0:30",
    description: "Happy groups learning and laughing during a cocktail class",
    voiceover: "\"From intimate private sessions to unforgettable group experiences.\"",
    visualNotes: "Diverse groups engaged, instructor demonstrating, participants trying techniques, genuine laughter",
    image: "https://readdy.ai/api/search-image?query=Group%20of%20happy%20people%20at%20cocktail%20making%20class%20learning%20from%20instructor%20laughing%20and%20having%20fun%20elegant%20venue%20warm%20lighting%20diverse%20group%20corporate%20team%20building%20event&width=400&height=300&seq=storyboard4&orientation=landscape",
    mood: "Fun & Engaging"
  },
  {
    id: 5,
    title: "Signature Creations",
    duration: "0:30 - 0:40",
    description: "Montage of stunning signature cocktails being garnished",
    voiceover: "\"Discover our signature creations, each one a masterpiece.\"",
    visualNotes: "Quick cuts of different cocktails, garnish placement, final presentation, steam/smoke effects",
    image: "https://readdy.ai/api/search-image?query=Beautiful%20signature%20cocktails%20lineup%20with%20elegant%20garnishes%20citrus%20twists%20herbs%20edible%20flowers%20colorful%20drinks%20professional%20presentation%20dark%20moody%20background%20premium%20bar%20photography&width=400&height=300&seq=storyboard5&orientation=landscape",
    mood: "Creative & Stunning"
  },
  {
    id: 6,
    title: "Mobile Bar Service",
    duration: "0:40 - 0:50",
    description: "Elegant mobile bar setup at various event locations",
    voiceover: "\"We bring the bar to you - weddings, corporate events, private parties.\"",
    visualNotes: "Different venue setups, outdoor garden party, rooftop event, elegant wedding reception",
    image: "https://readdy.ai/api/search-image?query=Elegant%20mobile%20cocktail%20bar%20setup%20at%20outdoor%20garden%20wedding%20reception%20with%20string%20lights%20professional%20bartender%20serving%20guests%20beautiful%20evening%20atmosphere%20premium%20event%20catering&width=400&height=300&seq=storyboard6&orientation=landscape",
    mood: "Versatile & Elegant"
  },
  {
    id: 7,
    title: "The Toast",
    duration: "0:50 - 0:55",
    description: "Glasses clinking in celebration, golden liquid catching light",
    voiceover: "\"Create memories that last a lifetime.\"",
    visualNotes: "Slow motion clink, champagne bubbles, warm smiles in background, golden hour lighting",
    image: "https://readdy.ai/api/search-image?query=Elegant%20champagne%20glasses%20clinking%20toast%20celebration%20golden%20liquid%20catching%20light%20bubbles%20rising%20warm%20bokeh%20background%20luxury%20celebration%20moment%20cinematic%20slow%20motion%20feel&width=400&height=300&seq=storyboard7&orientation=landscape",
    mood: "Celebratory & Warm"
  },
  {
    id: 8,
    title: "Call to Action",
    duration: "0:55 - 1:00",
    description: "Logo with contact information and booking prompt",
    voiceover: "\"House of FMR - Book your experience today.\"",
    visualNotes: "Logo animation, website URL, social handles, 'Book Now' button animation, fade to black",
    image: "https://readdy.ai/api/search-image?query=Elegant%20brand%20logo%20end%20screen%20with%20contact%20information%20dark%20luxurious%20background%20golden%20accents%20book%20now%20call%20to%20action%20professional%20video%20outro%20premium%20branding&width=400&height=300&seq=storyboard8&orientation=landscape",
    mood: "Professional & Direct"
  }
];

export default function VideoStoryboard() {
  const [activeScene, setActiveScene] = useState<number>(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setActiveScene((prev) => {
        if (prev >= storyboardScenes.length) {
          setIsPlaying(false);
          return 1;
        }
        return prev + 1;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const handlePlaythrough = () => {
    setIsPlaying(true);
    setActiveScene(1);
  };

  const activeSceneData = storyboardScenes.find(s => s.id === activeScene);

  return (
    <section className="py-24 bg-gradient-to-b from-neutral-950 to-neutral-900" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium mb-6">
            <i className="ri-film-line mr-2"></i>
            Production Planning
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Promo Storyboard
          </h2>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
            Scene-by-scene breakdown for your House of FMR promotional content. 
            Click each scene to preview or play through the entire sequence.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Scene Preview with Auto-cycling */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="sticky top-24">
              {/* Main Preview */}
              <div className="relative rounded-2xl overflow-hidden bg-neutral-800 aspect-video mb-6">
                <img 
                  src={activeSceneData?.image} 
                  alt={activeSceneData?.title}
                  className="w-full h-full object-cover transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                
                {/* Scene Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
                      Scene {activeSceneData?.id}
                    </span>
                    <span className="px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-full backdrop-blur-sm">
                      {activeSceneData?.duration}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {activeSceneData?.title}
                  </h3>
                  <p className="text-neutral-300 text-sm">
                    {activeSceneData?.description}
                  </p>
                </div>

                {/* Playing Indicator */}
                {isPlaying && (
                  <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 bg-red-500 rounded-full">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    <span className="text-white text-xs font-bold">PLAYING</span>
                  </div>
                )}
              </div>

              {/* Scene Details Card */}
              <div className="bg-neutral-800/50 backdrop-blur-sm rounded-xl p-6 border border-neutral-700">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
                      <i className="ri-mic-line mr-2"></i>Voiceover
                    </h4>
                    <p className="text-white text-sm italic">
                      {activeSceneData?.voiceover}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
                      <i className="ri-emotion-line mr-2"></i>Mood
                    </h4>
                    <p className="text-white text-sm">
                      {activeSceneData?.mood}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-neutral-700">
                  <h4 className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
                    <i className="ri-vidicon-line mr-2"></i>Visual Notes
                  </h4>
                  <p className="text-neutral-300 text-sm">
                    {activeSceneData?.visualNotes}
                  </p>
                </div>
              </div>

              {/* Playthrough Button */}
              <button
                onClick={handlePlaythrough}
                disabled={isPlaying}
                className={`w-full mt-6 py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                  isPlaying 
                    ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500 hover:shadow-lg hover:shadow-amber-500/30'
                }`}
              >
                {isPlaying ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Playing Scene {activeScene} of {storyboardScenes.length}...
                  </>
                ) : (
                  <>
                    <i className="ri-play-circle-line text-xl"></i>
                    Play Full Storyboard Preview
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Scene Timeline */}
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-bold text-lg">
                <i className="ri-list-ordered mr-2 text-amber-400"></i>
                Scene Timeline
              </h3>
              <span className="text-neutral-400 text-sm">
                Total Duration: 1:00
              </span>
            </div>

            <div className="space-y-3">
              {storyboardScenes.map((scene) => (
                <button
                  key={scene.id}
                  onClick={() => !isPlaying && setActiveScene(scene.id)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 group ${
                    activeScene === scene.id
                      ? 'bg-amber-500/20 border-2 border-amber-500'
                      : 'bg-neutral-800/50 border-2 border-transparent hover:bg-neutral-800 hover:border-neutral-600'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Scene Number */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold transition-all ${
                      activeScene === scene.id
                        ? 'bg-amber-500 text-black'
                        : 'bg-neutral-700 text-neutral-400 group-hover:bg-neutral-600'
                    }`}>
                      {scene.id}
                    </div>

                    {/* Scene Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`font-bold truncate transition-colors ${
                          activeScene === scene.id ? 'text-amber-400' : 'text-white'
                        }`}>
                          {scene.title}
                        </h4>
                        <span className="text-neutral-500 text-xs flex-shrink-0">
                          {scene.duration}
                        </span>
                      </div>
                      <p className="text-neutral-400 text-sm line-clamp-2">
                        {scene.description}
                      </p>
                    </div>

                    {/* Thumbnail */}
                    <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 hidden sm:block">
                      <img 
                        src={scene.image} 
                        alt={scene.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Progress Bar for Active Scene */}
                  {activeScene === scene.id && isPlaying && (
                    <div className="mt-3 h-1 bg-neutral-700 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full animate-progress"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Export Options */}
            <div className="mt-8 p-6 bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-xl border border-amber-500/20">
              <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                <i className="ri-download-cloud-line text-amber-400"></i>
                Ready to Produce?
              </h4>
              <p className="text-neutral-400 text-sm mb-4">
                Use this storyboard as a guide for your content production or share it with your creative team.
              </p>
              <div className="flex flex-wrap gap-3">
                <button className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-all flex items-center gap-2 whitespace-nowrap">
                  <i className="ri-file-pdf-line"></i>
                  Export as PDF
                </button>
                <button className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-all flex items-center gap-2 whitespace-nowrap">
                  <i className="ri-share-line"></i>
                  Share Link
                </button>
                <button className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-all flex items-center gap-2 whitespace-nowrap">
                  <i className="ri-printer-line"></i>
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Video Script Summary */}
        <div className={`mt-16 transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-neutral-800/30 rounded-2xl p-8 border border-neutral-700">
            <h3 className="text-2xl font-bold text-white mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              <i className="ri-draft-line mr-3 text-amber-400"></i>
              Full Script
            </h3>
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-invert">
                <p className="text-neutral-300 text-center italic text-lg leading-relaxed">
                  "Where passion meets perfection... Welcome to House of FMR, Sydney's premier mobile cocktail experience. 
                  Master the art of mixology with our expert-led classes. From intimate private sessions to unforgettable group experiences. 
                  Discover our signature creations, each one a masterpiece. We bring the bar to you - weddings, corporate events, private parties. 
                  Create memories that last a lifetime. House of FMR - Book your experience today."
                </p>
              </div>
              <div className="flex justify-center gap-6 mt-8 text-sm text-neutral-400">
                <span><i className="ri-time-line mr-2 text-amber-400"></i>Duration: 60 seconds</span>
                <span><i className="ri-film-line mr-2 text-amber-400"></i>8 Scenes</span>
                <span><i className="ri-slideshow-line mr-2 text-amber-400"></i>Auto-cycling Preview</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress 3s linear;
        }
      `}</style>
    </section>
  );
}